import React, { useState, useEffect, useMemo } from "react";
import { GoogleMap, LoadScript, Marker, InfoWindow } from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import ErrorModal from "../Security/ErrorModal";
import ProviderFormModal from "../FindCare/ProviderForm";
import { useLocation, useNavigate } from "react-router-dom";
import stateCitiesData from "../../Common/statescities.json";
import CustomDropdown from "../../CustomControls/dropdown";
import { Endpoints } from "../../Global/Endpoints";
import { useTranslation } from 'react-i18next';
import ChatBotWindow from "../ChatBot/ChatBotWindow";
import { useChat } from '../Layout/ChatContext';
import { Multiselect } from "multiselect-react-dropdown";
import MultiSelectDropdown from "../../CustomControls/MultiSelectDropdown";

const MapSearch = () => {
  const { t, i18n } = useTranslation();
  const { isChatVisible, toggleChat, showChat } = useChat();
  const [isLoading, setIsLoading] = useState(false);
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState({ lat: 37.7749, lng: -122.4194 }); // Default location (San Francisco)
  const [locationInput, setLocationInput] = useState("");
  const navigate = useNavigate();
  const [providerType, setProviderType] = useState('');
  const token = localStorage.getItem('AccessToken');
  const [markers, setMarkers] = useState([]);
  const [searchType, setSearchType] = useState("practice");
  const userInfo = useSelector((state) => state.userData);
  const location = useLocation();

  // Get external data from location state if it exists
  const externalData = location.state?.externalData;

  // Support direct access via query params if navigation state is missing
  const searchParamsObj = new URLSearchParams(location.search);
  let urlExternalData = null;
  if (!externalData && (searchParamsObj.get('MemberID') && searchParamsObj.get('Contract') && searchParamsObj.get('DeviceType'))) {
    urlExternalData = {
      MemberID: searchParamsObj.get('MemberID'),
      Contract: searchParamsObj.get('Contract'),
      DeviceType: searchParamsObj.get('DeviceType'),
      EmailID: searchParamsObj.get('EmailID') || ""
    };
  }
  // Use either external data from state, or from URL query params
  const effectiveExternalData = externalData || urlExternalData;
  // Use either external data or Redux data
  const effectiveUser = useMemo(() => {
    if (effectiveExternalData) {
      return {
        userData: {
          ...effectiveExternalData,
          ContractIdentified: effectiveExternalData.Contract,
          DeviceType: effectiveExternalData.DeviceType || "PatientPortal",
          EmailID: effectiveExternalData.EmailID || ""
        }
      };
    }
    return userInfo;
  }, [effectiveExternalData, userInfo]);

  const [showModal, setShowModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [searchParams, setSearchParams] = useState({
    country: "",
    state: "",
    city: "",
    zipcode: "",
    mile: "",
    BIPOC: "",
    LGBTQIA: "",
    diagnosis: [],
  });
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [zoom, setZoom] = useState(15);

  const [isActive, setIsActive] = useState(false);
  const [diagnosisOptions, setDiagnosisOptions] = useState([]);

  const [showAll, setShowAll] = useState(false);
  const [showStateSearch, setShowStateSearch] = useState(false);
  const [showZipCodeSearch, setShowZipCodeSearch] = useState(false);
  const [showPracticeProviderSearch, setShowPracticeProviderSearch] = useState(false); // NEW: controls Practice/Provider Name search visibility

  const visibleMarkers = showAll ? markers : markers.slice(0, 3);


  const selectedProviderType = location.state?.selectedProviderType;
  const userChoice = location.state?.userChoice;

  const selectedCategories = location.state?.selectedCategories;
  const [practiceProviderOptions, setPracticeProviderOptions] = useState([]);
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [modalResolver, setModalResolver] = useState(null);
  const [specialAccessVisibility, setSpecialAccessVisibility] = useState(null);
  // Generate distance options from 1 to 125 in increments of 5
  const distanceOptions = [1, 3, ...Array.from({ length: 25 }, (_, i) => (i + 1) * 5)];

  const handleInputChange = (e) => {
    //setSearchParams({ ...searchParams, [e.target.name]: e.target.value });
    const { name, value } = e.target;

    let newValue = value;
  if (name === "zipcode" && isHighmarkPortal) {
    newValue = value.replace(/\D/g, ""); // Only digits
  }

    setSearchParams((prevParams) => {
      const updatedParams = { ...prevParams, [name]: newValue };

      if (name === 'country') {
        // Clear state and city when country changes
        updatedParams.state = '';
        updatedParams.city = '';
        setCities([]);
      }

      if (name === 'state') {
        // Clear city when state changes
        updatedParams.city = '';
      }

      return updatedParams;
    });
  };
  useEffect(() => {
    if (location.state) {
      if (location.state.cancelandbookappoinment === "Y") {
        setProviderType(location.state.selectedProviderType);
      }
      else {
        setProviderType(location.state.selectedProviderType);
      }
    }

  }, [location.state]);

  // Fetch options for the Practice/Provider dropdown
  useEffect(() => {
    if (searchType === "practice") {
      
      const fetchPracticeProviderList = async () => {
        try {
          const params = {
            MemberID: effectiveUser.userData.MemberID,
            Contract: effectiveUser.userData.ContractIdentified,
            DeviceType: effectiveUser.userData.DeviceType || "PatientPortal",
            EmailID: effectiveUser.userData.EmailID,
            coutry: searchParams.country, // Use selected country if available
          };
          setIsLoading(true);
          const response = await axios.post(
            Endpoints.LoadGroupNames,
            { _Params: JSON.stringify(params) },
            {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                // Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.data && Array.isArray(response.data)) {
            setPracticeProviderOptions(response.data.map(item => item.GroupName));
          }
        } catch (err) {
          setPracticeProviderOptions([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchPracticeProviderList();
    }
  }, [searchParams.country, effectiveUser.userData, token, searchType]);
  useEffect(() => {
    // Check if the appointment data is provided
    if (location.state && location.state.appointment && map) {
      const { Address, City, State, ZipCode, ProviderName } = location.state.appointment;
      // Construct a full address string
      const fullAddress = `${Address}, ${City}, ${State} ${ZipCode}`;

      // Use Google Maps Geocoder to convert the address to lat/lng
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: fullAddress }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          const appointmentMarker = {
            lat: loc.lat(),
            lng: loc.lng(),
            name: ProviderName,
            address: fullAddress,
            isAppointment: true // Mark this marker as an appointment marker
          };

          // Option 1: Add the appointment marker to your markers array
          //setMarkers((prevMarkers) => [...prevMarkers, appointmentMarker]);

          // Option 2: If you want to solely display the appointment marker, you could set markers directly:
          setMarkers([appointmentMarker]);

          // Center the map on the appointment location
          setCenter({ lat: loc.lat(), lng: loc.lng() });
        } else {
          console.error("Geocode was not successful for the following reason: " + status);
        }
      });
    }
  }, [location.state, map]);
  useEffect(() => {
    const fetchBenefits = async () => {
      // For external users, fetch UnderstandingBenefits and handle both UI and access control
      if (effectiveExternalData) {
        setIsLoading(true); // Show loader while API call is in progress
        try {
          const payload = {
            _Params: JSON.stringify({
              MemberID: effectiveExternalData.MemberID || "",
              contract: effectiveExternalData.Contract || "",
              EmailID: effectiveExternalData.EmailID || "",
              DeviceType: effectiveExternalData.DeviceType || "PatientPortal"
            })
          };
          const response = await axios.post(Endpoints.UnderstandingBenefits, payload, {
            headers: { "Content-Type": "application/x-www-form-urlencoded" }
          });
          // Set UI toggles
          setShowStateSearch(response.data.StateSearch === "Y");
          setShowZipCodeSearch(response.data.ZipCodesearch === "Y");
          if (response.data.ZipCodesearch === "Y") {
            setSearchType("zip");
          } else if (response.data.StateSearch === "Y") {
            setSearchType("city");
          }
          // Access control: redirect if not Premiere
          // if (response.data.Premier !== "Yes") {
          //   const params = new URLSearchParams({
          //     MemberID: effectiveExternalData.MemberID || "",
          //     Contract: effectiveExternalData.Contract || "",
          //     DeviceType: effectiveExternalData.DeviceType || "PatientPortal"
          //   }).toString();
          //   setIsLoading(false); // Hide loader before redirect
          //   navigate(`/load-findcare-list?${params}`, { replace: true });
          //   return;
          // }
        } catch (error) {
          // On error, treat as not premier
          const params = new URLSearchParams({
            MemberID: effectiveExternalData.MemberID || "",
            Contract: effectiveExternalData.Contract || "",
            DeviceType: effectiveExternalData.DeviceType || "PatientPortal"
          }).toString();
          setIsLoading(false); // Hide loader before redirect
          navigate(`/load-findcare-list?${params}`, { replace: true });
          return;
        } finally {
          setIsLoading(false); // Always hide loader after API call
        }
        return;
      }
      // For authenticated users, check permissions through the API
      setIsLoading(true); // Show loader for authenticated users as well
      const payload = {
        _Params: JSON.stringify({
          MemberID: effectiveUser.userData.MemberID || "",
          EmailID: effectiveUser.userData.EmailID || "",
          DeviceType: effectiveUser.userData.DeviceType || "PatientPortal"
        }),
      };
      try {
         debugger;
        const response = await axios.post(Endpoints.UnderstandingBenefits, payload, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });
        // Set visibility based on response data
        const { StateSearch, ZipCodesearch, ProviderSearch, ContractType } = response.data;

        setShowStateSearch(StateSearch === "Y"); // Show state search if 'Y'
        setShowZipCodeSearch(ZipCodesearch === "Y"); // Show zip code search if 'Y'
        setShowPracticeProviderSearch(ProviderSearch === "Y"); // Show Practice/Provider Name search if 'Y'

        // Set search type based on ContractType flag if present
        if (ContractType === "Z") {
          setSearchType("zip"); // Default to zip search if ContractType is 'Z'
        } else if (ContractType === "S") {
          setSearchType("city"); // Default to city/state search if ContractType is 'S'
        } else if (ContractType === "P") {
          setSearchType("practice"); // Default to provider/practice search if ContractType is 'P'
        } else {
          // Fallback to previous logic based on response data
          if (ZipCodesearch === "Y") {
            setSearchType("zip"); // Set default to "zip" if ZipCodesearch is "Y"
          } else if (StateSearch === "Y") {
            setSearchType("city"); // Set default to "city" if StateSearch is "Y"
          } else if (ProviderSearch === "Y") {
            setSearchType("practice"); // Set default to "practice" if ProviderSearch is "Y"
          }
        }
      } catch (error) {
        console.error("Error fetching benefits:", error);
      } finally {
        setIsLoading(false); // Always hide loader after API call
      }
    };
    fetchBenefits();
  }, [effectiveUser.userData, effectiveExternalData, navigate]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const params = {
          EmailID: effectiveUser.userData.EmailID || "",
          DeviceType: effectiveUser.userData.DeviceType || "PatientPortal",
        };

        const response = await axios.post(
          Endpoints.LoadCountry,
          { _Params: JSON.stringify(params) },
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              // Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    }
    fetchCountries();
  }, [searchType, effectiveUser.userData]);

  useEffect(() => {
    const fetchStates = async () => {
      if (!searchParams.country) return; // Exit if no country is selected

      try {
        const params = {
          Country: searchParams.country,
          EmailID: userInfo.userData.EmailID || "",
          DeviceType: "PatientPortal",
        };
        const response = await axios.post(
          Endpoints.LoadState,
          { _Params: JSON.stringify(params) },
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              // Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data) {
          console.log("States response:", response.data);
          setStates(response.data);
        }
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };

    fetchStates();
  }, [searchParams.country]);


  useEffect(() => {
    const fetchCities = async () => {
      if (!searchParams.state) return; // Exit if no state is selected

      try {
        const params = {
          State: searchParams.state,
          EmailID: userInfo.userData.EmailID,
          DeviceType: "PatientPortal",
        };
        const response = await axios.post(
          Endpoints.LoadCity,
          { _Params: JSON.stringify(params) },
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              // Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data) {
          setCities(response.data);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };

    fetchCities();
  }, [searchParams.state]);
  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    // Clear search params
    setSearchParams({
      ...searchParams,
      country: "",
      city: "",
      state: "",
      zipcode: "",
      mile: "",
      practiceProvider: "",
      diagnosis: []
    });
    // Clear search results
    setMarkers([]);
    setSelectedLocation(null);
    setSelectedMarker(null);
    setShowAll(false);
  };

  const handleSelect = (option, updatedSelectedOptions) => {

    if (option.name === "None") {
      // Only "None" selected
      setSearchParams({
        ...searchParams,
        diagnosis: ["None"],
      });
    } else if (option.name === "Select All") {
      // Only "Select All" should be selected
      setSearchParams({
        ...searchParams,
        diagnosis: ["Select All"],
      });
    } else {
      // Normal option selected
      // Remove "None" and "Select All" if they exist
      const filtered = updatedSelectedOptions
        .map(item => item.name)
        .filter(name => name !== "None" && name !== "Select All");

      setSearchParams({
        ...searchParams,
        diagnosis: filtered,
      });
    }
  };

  // ------------- handlePracticeProviderChange
  const handlePracticeProviderChange = (selectedValue) => {
    setSearchParams((prev) => ({
      ...prev,
      practiceProvider: selectedValue,
    }));
  };
  const handleRemove = (option, updatedSelectedOptions) => {
    // Handle removal of an item
    setSearchParams({
      ...searchParams,
      diagnosis: updatedSelectedOptions.map(item => item.name),
    });
  };
  const [mapHeight, setMapHeight] = useState(window.innerHeight - 165); // Initial height (adjust as needed)

  useEffect(() => {
    const handleResize = () => {
      setMapHeight(window.innerHeight - 165); // Update height on resize
    };
    console.log("mapHeight", mapHeight);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    fetchDiagnosisOptions();
  }, []);

  const fetchDiagnosisOptions = async () => {
    try {
      const params = {
        ProviderType: selectedProviderType,
        MemberID: effectiveUser.userData.MemberID,
        Contract: effectiveUser.userData.ContractIdentified,
        DeviceType: effectiveUser.userData.DeviceType || "PatientPortal",
        EmailID: effectiveUser.userData.EmailID || "",
      }
      const response = await axios.post(
        Endpoints.LoadDiagnosisDropDownValues,
        { _Params: JSON.stringify(params) },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Connection": "close",
            "Accept": "*/*",
            // Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        // Filter out "None" from the response data
        //const filteredDiagnosisOptions = response.data.filter(option => option !== "None" && option !== "Select All");

        // Set the filtered diagnosis options to state
        setDiagnosisOptions(response.data);
      }
    } catch (error) {
      console.error("Error fetching diagnosis options:", error);
    }
  };
  const handleSearch = async () => {
    if (searchType === "city") {
      if (!searchParams.country) {
        setModalMessage(t('selectcountri'));
        setShowModal(true);
        setIsLoading(false); // Stop loading on validation error
        return;
      }
      if (!searchParams.state) {
        setModalMessage(t('selectstate'));
        setShowModal(true);
        return;
      }
      if (!searchParams.city) {
        setModalMessage(t('selectcity'));
        setShowModal(true);
        return;
      }
    }
    if (searchType === "zip") {
      if (!searchParams.country) {
        setModalMessage(t('selectcountri'));
        setShowModal(true);
        setIsLoading(false); // Stop loading on validation error
        return;
      }
      
      if (!searchParams.zipcode) {
        setModalMessage(t('selectzipcode'));
        setShowModal(true);
        return;
      }
      else if (!searchParams.mile) {
        setModalMessage(t('selectdistance'));
        setShowModal(true);
        return;
      }
    }
    if (searchType === "practice" && !searchParams.practiceProvider) {
      setModalMessage("Please select a provider name.");
      setShowModal(true);
      return;
    }
    try {
      setIsLoading(true); // Start loading state

      // Initialize all category properties to an empty string
      const initialParams = {
        AtBirth: "",
        Postpartum: "",
        LactationSupport: "",
        Prenatal: "",
        AllCategories: "",
      };
      console.log("selectedcatg:", selectedCategories)
      const formatCategoryName = (selectedCategories) => selectedCategories.replace(/\s+/g, '');
      // Set properties to "Y" for each selected category
      if (selectedProviderType === "Doulas") {
        selectedCategories.forEach((selectedCategories) => {
          const normalizedCategory = formatCategoryName(selectedCategories);
          if (initialParams.hasOwnProperty(normalizedCategory)) {
            initialParams[normalizedCategory] = "Y";
          }
        });
      }
      // if(selectedProviderType==="Reproductive Endocrinologists/Infertility"){
      //   Eggfreezflag= userChoice,
      //   PracticeName= searchParams.practiceProvider || "";
      // }
      const params = {
        ...searchParams,
        ProviderType: selectedProviderType,
        MemberID: effectiveUser.userData.MemberID,
        Contract: effectiveUser.userData.ContractIdentified,
        DeviceType: effectiveUser.userData.DeviceType || "PatientPortal",
        EmailID: effectiveUser.userData.EmailID || "",
        Eggfreezflag: userChoice, // Set Eggfreezflag based on user choice
        PracticeName: searchParams.practiceProvider || "", // Set PracticeName based on user input
        ...initialParams,
      };

      // Only pass country if searching by country (city search type)
    // if (searchType === "zip") {
    //   params.country = "";
    // }
      // Convert diagnosis array to string
      if (params.diagnosis && params.diagnosis.length > 0) {
        // Remove "Select All" from the selected diagnoses
        //const filteredDiagnosis = params.diagnosis.filter(item => item !== "Select All");

        // If there are remaining diagnoses, join them into a string
        //params.Diagnosis = filteredDiagnosis.length > 0 ? filteredDiagnosis.join(", ") : "";

        params.Diagnosis = params.diagnosis.join(", ");

      } else {
        // If no diagnosis is selected, set Diagnosis to empty string
        params.Diagnosis = "";
      }
      delete params.diagnosis;
      console.log("params", params);
      const response = await axios.post(
        Endpoints.LoadZipcode,
        { _Params: JSON.stringify(params) },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Connection": "close",
            "Accept": "*/*",
            // Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data && response.data.length > 0) {
        const locations = response.data.map((item) => ({
          lat: item.Location.Latitude,
          lng: item.Location.Longitude,
          name: item.GroupName,
          address: item.Address,
          phone: item.PhoneNumber,
          profileUrl: item.PracticeProfile,
          distance: item.DistanceText,
          isFavourite: item.IsFavourite,
          distanceValue: item.DistanceValue,
          durationText: item.DurationText,
          groupId: item.GroupID,
          groupName: item.GroupName,
          city: item.City,
          state: item.State,
          text: item.Text,
          bipoc: item.BIPOC,
          lgbtqia: item.LGBTQIA,
          specialAccess: item.SpecialAccess,
          isFavourite: item.IsFavourite,
          isStoredData: item.IsStoredData,
          website: item.Website,
          email: item.Email,
          doulaType: item.DoulaType
        }));
        setZoom(12);
        console.log("locations:", response.data)
        setMarkers(locations);
        setCenter({
          lat: locations[0].lat,
          lng: locations[0].lng,
        });

        map.panTo({
          lat: locations[0].lat,
          lng: locations[0].lng,
        });

        // Calculate bounds to fit all markers
        const bounds = new window.google.maps.LatLngBounds();

        locations.forEach((location) => {
          bounds.extend(new window.google.maps.LatLng(location.lat, location.lng));
        });

        map.fitBounds(bounds); // Adjust the map view to fit all markers

        setShowAll(false)
      } else {
        console.log("No results found.");
        setMarkers([]);
        // setModalMessage("No results found.");
        // setShowModal(true);
        fetchProviders();
      }
    } catch (error) {
      console.error("Error fetching provider data:", error);
      console.log("Failed to fetch data. Please try again.");
      setMarkers([]);
      setModalMessage(t('failedtofetchdata'));
      setShowModal(true);
    } finally {
      setIsLoading(false); // Stop loading state whether request succeeded or failed
    }
  };
  const handleLocationClick = (index) => {
    setZoom(15);
    setSelectedLocation(index);
    const selectedLoc = markers[index];
    if (map && selectedLoc) {
      map.panTo({
        lat: selectedLoc.lat,
        lng: selectedLoc.lng,
      });
    }
  };
  const handleMarkerClick = (marker, index) => {
    setIsActive(true);
    setSelectedMarker({ ...marker, index });
  };
  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
  };
  const handleModalClose = () => {
    setShowModal(false);
  }
  const handleProviderModalClose = () => {
    setShowProviderModal(false);
  }
  const handleSuggestProviderClick = () => {

    setShowProviderModal(true);
  };
  const handleBackClick = () => {
    navigate(-1); // Go back to the previous page
  };

  const StateCityDropdown = () => {
    // State for selected state and cities
    const [selectedState, setSelectedState] = useState('');
    const [cities, setCities] = useState([]);

    // Handle state change
    const handleStateChange = (event) => {
      const selected = event.target.value;
      setSelectedState(selected);
      // Find the cities array based on selected state
      const stateInfo = stateCitiesData.find(state => state.state === selected);
      setCities(stateInfo ? stateInfo.cities : []);
    };
  }

  const fetchProviders = async () => {
    try {
      const params = {
        MemberID: effectiveUser.userData.MemberID,
        Contract: effectiveUser.userData.ContractIdentified,
        DeviceType: effectiveUser.userData.DeviceType || "PatientPortal",
        EmailID: effectiveUser.userData.EmailID || ""
      };

      const response = await axios.post(
        Endpoints.loadprovidersearchfeed,
        `_Params=${encodeURIComponent(JSON.stringify(params))}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            // Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data) {
        const specialAccessItem = response.data.find(
          (provider) => provider.ProviderType === "Special Access Provider Available"
        );

        if (specialAccessItem) {
          setSpecialAccessVisibility(specialAccessItem.Visibility); // Save "Yes" or "No"
        }

      } else {
        console.log(t('noprovidersfound'));
      }
    } catch (error) {
      console.error(t('errorfetchingproviders'), error);
      console.log(t('errorfetchingproviders'));
    }
  };



  const askNotifyProvider = (location) => {
    return new Promise((resolve) => {
      // If already favorite → no popup, resolve immediately
      if (location.isFavourite === "Y") {
        resolve(true);
        return;
      }

      setModalResolver(() => resolve); // Store resolve to call on Yes/No
      setShowFavoriteModal(true);
    });
  };


  const handleToggleFavorite = async (location, index) => {
    try {
      // if (location.isFavourite === "N") {
      //   var notifyValue = await askNotifyProvider();
      // }
      // if (notifyValue === "N") {
      //   var newStatus = notifyValue;

      // }
      // else {
      //   var newStatus = location.isFavourite === "Y" ? "N" : "Y";

      // }
      
       const notifyValue = await askNotifyProvider(location);
          // Define the new status
          const newStatus = location.isFavourite === "Y" ? "N" : "Y";
      // Define the new status

      // Update the favorite status in the state immediately for a responsive UI
      setMarkers((prevMarkers) => {
        const updatedMarkers = [...prevMarkers];
        updatedMarkers[index] = {
          ...location,
          isFavourite: newStatus,
        };
        return updatedMarkers;
      });
      // Parameters for the API call
      const params = {
        Contract: effectiveUser.userData.ContractIdentified,
        PhoneNumber: location.phone || "", // Ensure the phone number is included if available
        SpecialAccess: location.specialAccess || null, // Add other properties as needed
        City: location.city || "",
        PracticeProfile: location.practiceProfile || "",
        IsStoredData: true,
        DurationText: location.durationText || "",
        LGBTQIA: location.lgbtqia || "N",
        Location: {
          Latitude: location.lat,
          Longitude: location.lng,
        },
        DistanceValue: location.distanceValue || 0,
        State: location.state || "",
        Address: location.address || "",
        BIPOC: location.bipoc || "N",
        GroupID: location.groupId || "",
        GroupName: location.name || "",
        Text: location.address || "",
        MemberID: effectiveUser.userData.MemberID,
        DurationValue: location.durationValue || 0,
        IsFavourite: newStatus,
        NotifyProvider: notifyValue,
        DistanceText: location.distance || "",
        DeviceType: effectiveUser.userData.DeviceType || "PatientPortal",
        EmailID: effectiveUser.userData.EmailID || "",
        Website: location.website,
        Email: location.email,
        ProviderType: selectedProviderType
      };
      console.log("paramssavefav", params);

      // Send POST request to update favorite status
      const response = await axios.post(Endpoints.SaveZipcode,
        { _Params: JSON.stringify(params) },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Connection": "close",
            "Accept": "*/*",
            // Authorization: `Bearer ${token}`,
          },
        }
      );

      // Check if the response indicates success
      if (response.data === "Success") {
        console.log(t('favouritestatusupated'));
        handleSearch();
      } else {
        console.error("Error updating favorite status", response.data);
        // Revert the change in the UI if API fails
        setMarkers((prevMarkers) => {
          const revertedMarkers = [...prevMarkers];
          revertedMarkers[index] = {
            ...location,
            isFavourite: location.isFavourite,
          };
          return revertedMarkers;
        });
      }
    } catch (error) {
      console.error("Error:", error);
      // Revert the change in case of an error
      setMarkers((prevMarkers) => {
        const revertedMarkers = [...prevMarkers];
        revertedMarkers[index] = {
          ...location,
          isFavourite: location.isFavourite,
        };
        return revertedMarkers;
      });
    } finally {
      setShowFavoriteModal(false);
    }
  };
  const formatPhoneNumber = (phone) => {
    // Remove non-digit characters
    const cleaned = ('' + phone).replace(/\D/g, '');

    // Match standard 10-digit US phone format
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }

    // If not 10 digits, return as-is
    return phone;
  };

  useEffect(() => {
  if (isHighmarkPortal && countries.length > 0) {
    const usa = countries.find(c => c.ShortName === "United States of America");
    if (usa) {
      setSearchParams(prev => ({
        ...prev,
        country: usa.ShortName
      }));
    }
  }
}, [isHighmarkPortal, countries]);
  return (
    <div className="container-fixed my-5 my-sm-1">
      {isLoading && (
        <div className="loading-container">
          <div className="loading"></div>        
          <h2 className="loading-text">{t("loading")}</h2>
        </div>
      )}
      
      <LoadScript googleMapsApiKey="AIzaSyAXAQp0EsPebzhFtlWfZAazZ8ixOPLJnsE">

        {isHighmarkPortal ?
          <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
            <button className="win-back-btn" onClick={handleBackClick}>
              <span>
                <i className="bi bi-chevron-left"></i>
              </span>
            </button>
            <h2 className="page-heading">{t('searchprovider')}</h2>
          </div> :
          <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
            <button className="win-back-btn" onClick={handleBackClick}>
              <span>
                <i className="bi bi-chevron-left"></i>
                {t('Back')}
              </span>
            </button>
            <h2 className="page-heading">{t('searchprovider')}</h2>
          </div>
        }


        {/* <div className="col-md-12 col-lg-12 mt-md-4 mt-sm-0 inner-page-heading-wrapper">
          <button onClick={handleBackClick} className="win-back-btn">
            <span><i  className="bi bi-chevron-left"></i> {t('back')}</span>
          </button>
          <h2 className="page-heading">{t('searchprovider')}</h2>
        </div> */}        <div className="row map-search-wrapper-row">
          {/* Radio buttons for selecting search type */}
          <div className="col-md-12 mb-4">
            {(showPracticeProviderSearch || externalData) && (
              <label className="me-4 d-inline-flex align-items-center">
                <input
                  type="radio"
                  value="practice"
                  checked={searchType === "practice"}
                  onChange={handleSearchTypeChange}
                  className="form-check-input radio-btn win-form-check-input me-2"
                />
                Search by Practice/Provider Name
              </label>
            )}
            {/* Show zip code search for authenticated users with permission or external users */}
            {(showZipCodeSearch || effectiveExternalData) && (
              <label className="me-4 d-inline-flex align-items-center">
                <input
                  type="radio"
                  value="zip"
                  checked={searchType === "zip"}
                  onChange={handleSearchTypeChange}
                  className="form-check-input radio-btn win-form-check-input me-2"
                />
                {t('searhbyzipcode')}
              </label>
            )}
            {/* Show state/city search for authenticated users with permission or external users */}
            {(showStateSearch || effectiveExternalData) && (
              <label className="me-4 d-inline-flex align-items-center">
                <input
                  type="radio"
                  value="city"
                  checked={searchType === "city"}
                  onChange={handleSearchTypeChange}
                  className="form-check-input radio-btn win-form-check-input me-2"
                />
                {t('searchbystatecity')}
              </label>
            )}

          </div>

          {/* Conditional fields based on search type */}
          <div className="map-search-filters-wrapper">
          {/* <div className="row"> */}
          {searchType === "practice" ? (
            <>
            <div className="col-12 col-sm-6 col-md-4 col-lg-2 me-2 d-flex map-search-country-wrapper mb-2 mb-md-0">
               <CustomDropdown
                    options={countries
                      .filter((country) => country.ShortName) // filter out null or undefined
                      .map((country) => country.ShortName)
                      .sort((a, b) => a.localeCompare(b))}
                    selectedOption={searchParams.country}
                    onChange={(selectedCountry) =>
                      handleInputChange({ target: { name: 'country', value: selectedCountry } })
                    }
                    label={t('country')}
                    searchable={true}
                    placeholder={t('selectcountry1')}
                    className="map-search-country-input"
                    disabled={isHighmarkPortal} // <-- Disable for Highmark
                    mapSearchFlag={true}
                    parentSelected={true}
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-2 me-2 d-flex map-search-state-wrapper">
                  <CustomDropdown
                    options={practiceProviderOptions.map(opt => opt)}
                    selectedOption={practiceProviderOptions.find(opt => opt === searchParams.practiceProvider) || ""}
                    onChange={selectedLabel => {
                      const selected = practiceProviderOptions.find(opt => opt === selectedLabel);
                      setSearchParams(prev => ({ ...prev, practiceProvider: selected ? selected : "" }));
                    }}
                    label="Practice/Provider"
                    searchable={true}
                    placeholder="---Select Provider Name---"
                    className="map-search-practice-provider-input"
                  />
                </div></>) :

              searchType === "zip" ? (
                <>
                 <div className="col-12 col-sm-6 col-md-4 col-lg-2 me-2 d-flex map-search-country-wrapper mb-2 mb-md-0">
      <CustomDropdown
        options={countries
          .filter((country) => country.ShortName)
          .map((country) => country.ShortName)
          .sort((a, b) => a.localeCompare(b))}
        selectedOption={searchParams.country}
        onChange={(selectedCountry) =>
          handleInputChange({ target: { name: 'country', value: selectedCountry } })
        }
        label={t('country')}
        searchable={true}
        placeholder={t('selectcountry1')}
        className="map-search-country-input"
        disabled={isHighmarkPortal}
        mapSearchFlag={true}
        parentSelected={true}
      />
    </div>
                  <div className="col-12 col-sm-6 col-md-4 col-lg-2 map-search-zipcode-wrapper">
                    <input
                      type="text"
                      name="zipcode"
                      placeholder="Zip Code"
                      value={searchParams.zipcode}
                      onChange={handleInputChange}
                      className="form-control win-form-control mobile-view map-search-zipcode-input"
                    maxLength={50}
                    />
                  </div>
                  <div className="col-12 col-sm-6 col-md-4 col-lg-2 d-flex mb-2 mb-md-0 map-search-distance-wrapper">
                    <CustomDropdown
                      options={distanceOptions} // Pass the list of distance options
                      selectedOption={searchParams.mile} // Pass the current selected distance
                      onChange={(selectedMile) =>
                        handleInputChange({ target: { name: 'mile', value: selectedMile } })
                      } // Update mile on selection
                      label={t('distance')}
                      searchable={false} // Disable search if not required
                      placeholder={t('selectdistance1')} // Custom placeholder
                      className="mobile-view map-search-distance-input"
                    />

                  </div>
                </>
              ) : (
                <>
                  <div className="col-12 col-sm-6 col-md-4 col-lg-2 me-2 d-flex map-search-country-wrapper">
                    <CustomDropdown
                      options={countries
                        .filter((country) => country.ShortName) // filter out null or undefined
                        .map((country) => country.ShortName)
                        .sort((a, b) => a.localeCompare(b))}
                      selectedOption={searchParams.country}
                      onChange={(selectedCountry) =>
                        handleInputChange({ target: { name: 'country', value: selectedCountry } })
                      }
                      label={t('country')}
                      searchable={true}
                      placeholder={t('selectcountry1')}
                      className="map-search-country-input"
                      disabled={isHighmarkPortal} // <-- Disable for Highmark
                      mapSearchFlag={true}
                      parentSelected={true}
                    />
                  </div>
                  <div className="col-12 col-sm-6 col-md-4 col-lg-2 d-flex map-search-state-wrapper">
                    <CustomDropdown
                      options={states.map((state) => state.StateNames)
                        .sort((a, b) => a.localeCompare(b)) // <-- Sort alphabetically
                      } // Extract state names from the data
                      selectedOption={searchParams.state} // Pass the current selected state
                      onChange={(selectedState) =>
                        handleInputChange({ target: { name: 'state', value: selectedState } })
                      } // Update state on selection
                      label={t('state')}
                      searchable={true} // Enable search functionality
                      placeholder={t('selectstate1')} // Custom placeholder
                      className="map-search-state-input"
                      mapSearchFlag={true}
                      parentSelected={!!searchParams.country}
                    />
                  </div>
                  <div className="col-12 col-sm-6 col-md-4 col-lg-2 d-flex map-search-city-wrapper">
                    <CustomDropdown
                      options={cities.map((city) => city.CityNames)}
                      selectedOption={searchParams.city} // Pass the current selected city
                      onChange={(selectedCity) =>
                        handleInputChange({ target: { name: 'city', value: selectedCity } })
                      } // Update city on selection
                      label="City"
                      searchable={true} // Enable search functionality
                      placeholder="--Select City--" // Custom placeholder
                      className="map-search-city-input"
                      mapSearchFlag={true}
                      parentSelected={!!searchParams.state}
                    />
                  </div>

                </>
              )}


          { selectedProviderType !== "Doulas" && <div className="col-12 col-sm-6 col-md-5 col-lg-4 map-search-diagnosis-wrapper">

              <MultiSelectDropdown
                options={diagnosisOptions.map((option) => ({ name: option, id: option }))} // Map API response options
                selectedValues={searchParams.diagnosis.map((item) => ({ name: item, id: item }))}
                onSelect={handleSelect}
                onRemove={handleRemove}
                placeholder={searchParams.diagnosis && searchParams.diagnosis.length > 0 ? "" : "Diagnosis(optional, choose all that apply)"} // Conditional placeholder
              />
              <p className="mb-2 note-black-14">
                {/* {t('refineSearchMessage')} */}
                <strong>Want to refine your search?</strong> <i>Select your diagnosis to find providers who specialize in your health needs.</i>
              </p>
            </div>}


            {/* Checkboxes for additional search filters */}
            <div className="col-12 col-sm-6 col-md-2 pt-2 map-search-checkbox-wrapper">
              <label className="d-inline-flex align-items-center me-4">
                <input
                  type="checkbox"
                  name="BIPOC"
                  onChange={(e) =>
                    setSearchParams({ ...searchParams, BIPOC: e.target.checked ? "Y" : "" })
                  }
                  className="form-check-input radio-btn win-form-check-input me-2"
                />
                BIPOC
              </label>
              <label className="d-inline-flex align-items-center">
                <input
                  type="checkbox"
                  name="LGBTQIA"
                  onChange={(e) =>
                    setSearchParams({ ...searchParams, LGBTQIA: e.target.checked ? "Y" : "" })
                  }
                  className="form-check-input radio-btn win-form-check-input me-2"
                />
                LGBTQIA+
              </label>
               <div className="col-12 col-sm-6 col-md-2 pt-2 map-search-button-wrapper">
              <button onClick={handleSearch} className="win-btn-primary mobile-view">
                {t('startsearch')}
              </button>
            </div>
         
          </div>


          </div>
       
       
       </div>

           

        <div className="googlemap-and-result-wrapper mt-4">
          <div className="googlemap-wrapper">
            <GoogleMap
              onLoad={mapInstance => setMap(mapInstance)}
              center={center}
              zoom={zoom}
              mapContainerStyle={{ width: "100%", height: `${mapHeight}px` }}
              className="col-md-12"
            >
              {markers.map((marker, index) => (
                <Marker key={index} position={{ lat: marker.lat, lng: marker.lng }}
                  onClick={() => handleMarkerClick(marker, index)}
                  className={isActive ? 'gm-style-iw hide-google-pointer' : 'gm-style-iw'} />
              ))}
              {selectedMarker && (
                <InfoWindow
                  position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                  onCloseClick={handleInfoWindowClose}
                >
                  <div >
                    <h4 className="map-view-location-name">{selectedMarker.name}</h4><br />
                    <p className="map-view-location-address" >{selectedMarker.address}</p><br />
                    {selectedMarker.doulaType && (
                      <>
                        <p className="map-view-other-labels">Doula Type: <b>{selectedMarker.doulaType}</b></p>
                        <br />
                      </>
                    )}
                    {selectedMarker.distance && (
                      <>
                        <p className="map-view-other-labels">Distance: <b>{selectedMarker.distance}</b></p>

                      </>
                    )}
                    {selectedMarker.phone && (
                      <p className="map-view-other-labels">Phone: <b>{formatPhoneNumber(selectedMarker.phone)}</b></p>
                    )}
                    {selectedMarker.website && (
                      <p className="map-view-other-labels">Website: <b>{selectedMarker.website}</b></p>
                    )}
                    {selectedMarker.email && (
                      <p className="map-view-other-labels wrapemail">Email: <b>{selectedMarker.email}</b></p>
                    )}
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedMarker.name || selectedMarker.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="get-directions-link ms-auto"
                    >
                      {t('getdirection')}
                    </a>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </div>
          <div className="search-result-wrapper" style={{ display: "block", height: `${mapHeight}px` }}>
            {/* Display search results */}
            {markers.length > 0 && !effectiveExternalData && (
              <div className="ms-2 mt-2 mt-md-3">
                { selectedProviderType !== "Doulas" && (
                <button onClick={handleSuggestProviderClick} className="win-btn-secondary-w-content mobile-view">
                  {isHighmarkPortal ? t('highmarksuggestaprovider') : t('suggestaprovider')}
                </button>
                )}
                <p className="mt-3 mb-2">
                  {t('tailoredresults')}
                </p>
              </div>
            )}
            {markers.length > 0 ? (
              <div className="d-flex flex-column ms-2 mt-3 map-view-result-wrapper" style={{ height: `${mapHeight - 95}px` }}>
                {visibleMarkers
                  .sort((a, b) => (b.isFavourite === "Y") - (a.isFavourite === "Y"))
                  .map((location, index) => (
                    <div key={index}
                      onClick={() => handleLocationClick(index)}
                      className="map-view-address-wrapper"
                      style={{
                        backgroundColor: selectedLocation === index ? "#cfe2ff" : "#ffffff",
                      }}>
                      <h4 className="map-view-location-name">{location.name}</h4>
                      <p className="map-view-location-address">{location.address}</p>
                      {(location.bipoc || location.lgbtqia) && (
                        <div className="map-view-other-tags">
                          {location.bipoc === "Y" && <span className="map-other-tag"><span className="map-other-tag-icon-bipoc"></span>BIPOC</span>}
                          {location.lgbtqia === "Y" && <span className="map-other-tag"><span className="map-other-tag-icon-lgbtqia"></span>LGBTQIA</span>}
                        </div>
                      )}
                      {!location.isAppointment && (
                        <div>
                          <span className={`favorites-icon ${location.isFavourite === "Y" ? "favsaved" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent the location click handler from firing
                              debugger
                              handleToggleFavorite(location, index);
                            }}>
                          </span>
                        </div>
                      )}
                      {location.doulaType && (
                        <div className="map-view-other-labels">Doula Type: <b>{location.doulaType}</b></div>
                      )}
                      {location.distance && (
                        <>
                          <div className="d-flex space-between">
                            <div className="map-view-other-labels">Distance: <b>{location.distance}</b></div>
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location.name || location.address)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="get-directions-link-highmark ms-auto"
                            >
                              {t('getdirections')}
                            </a>
                          </div>
                        </>
                      )}
                      {location.phone && (
                        <div className="map-view-other-labels">Phone: <b>{formatPhoneNumber(location.phone)}</b></div>
                      )}
                      {location.website && (
                        <div className="map-view-other-labels">Website: <b>{location.website}</b></div>
                      )}
                      {location.email && (
                        <div className="map-view-other-labels wrapemail">Email: <b>{location.email}</b></div>
                      )}

                    </div>
                  ))}
                {!showAll && markers.length > 3 && (
                  <button className="show-more-btn show-more-locations-btn mobile-view" onClick={() => setShowAll(true)}>
                    {t('showmore')}
                  </button>
                )}
              </div>
            ) : (
              <div className="ms-2 pt-5 mt-0"></div>
            )}
          {markers.length === 0 && specialAccessVisibility && (
  <div className="ms-2 mt-2">
    {/* Doulas-specific logic */}
{
// selectedProviderType === "Doulas" ? (
//       searchParams.mile && !isNaN(parseFloat(searchParams.mile)) ? (
//         parseFloat(searchParams.mile) <= 50 ? (
//           <p className="special-access-message">
//             Special Access Provider Available. Please contact WIN.
//           </p>
//         ) : (
//           <p className="special-access-message">
//             No providers were found for your search criteria. Please change the details and try again.
//           </p>
//         )
//       ) : (
//         <p className="special-access-message">
//           No providers were found for your search criteria. Please change the details and try again.
//         </p>
//       )
//     ) :
     (    
      <>
        {/* General Special Access logic for non-Doulas */}
       {
  specialAccessVisibility === "No" && !isHighmarkPortal ? (
    <>
      Special Access Provider Available. Please contact WIN.
    </>
  ) : null
}
        {//specialAccessVisibility === "Yes" && !effectiveExternalData && selectedProviderType != "Doulas" ? (
         specialAccessVisibility === "Yes" && !effectiveExternalData ? (
          <p className="special-access-message">
            {!isHighmarkPortal ? (
              <>
                Special Access Provider Available – Please{" "}
                <span
                  className="suggest-provider-link"
                  style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                  onClick={handleSuggestProviderClick}
                >
                  Click here
                </span>{" "}
                to request a gap exception.
              </>
            ) : (
              <>
                We didn’t find any providers that match your current filters. Try expanding your search radius or adjusting your filters to see more options. If you know of a provider nearby who isn’t in our network, you can request to add them{" "}
                <span
                  className="suggest-provider-link"
                  style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                  onClick={handleSuggestProviderClick}
                >
                  here
                </span>
                .
              </>
            )}
          </p>
        ) : (
          <>
            {/* Removed mile-related logic here */}
          </>
        )}
      </>
    )}
  </div>
)}

            {/* External User Condition */}
            {markers.length === 0 && effectiveExternalData && specialAccessVisibility && (
              <div className="ms-2 mt-3">
                <p className="special-access-message">
                  No providers were found for your search criteria. Please try changing the details and search again.
                </p>
              </div>
            )}

          </div>
          <ErrorModal show={showModal} message={modalMessage} onClose={handleModalClose} />
          <ErrorModal
            show={showFavoriteModal}
            message={isHighmarkPortal? t("sendtoproviderhighmark"):t("sendtoprovider")}
            onNo={() => {
              if (modalResolver) modalResolver("N"); // User chose No
            }}
            onYes={() => {
              if (modalResolver) modalResolver("Y"); // User chose Yes
            }}
          />
        </div>
        <ProviderFormModal isOpen={showProviderModal} NoOfMarkers={markers.length} providerType={providerType} onClose={handleProviderModalClose} />
      </LoadScript>
    </div>
  );
}

export default MapSearch;